// eslint-disable-next-line no-use-before-define
import React, { ReactElement, useContext, useMemo } from 'react'

import _ from 'lodash'

import { useRegistry } from 'live/services/registry'
import i18n from 'live/services/i18n'
import TestToolWidgetActionContent, { TestToolWidgetAction } from 'plugin-assets/shared/test-tools/TestToolWidgetAction'
import { AssetChartPrototypeRightActionsEntry } from 'plugin-assets/shared/widget/multi-channel/getAssetChartPrototype'
import ChannelsWiTestToolsRegistry, {
    TestToolEntry
} from 'plugin-assets/shared/test-tools/multi-channel-test-tools-registry'
import { toJS } from 'mobx'
import { WidgetContext } from 'live/context/widget'

type Props = {
    className: string
    toggle: (value: boolean) => void
    visibleAssets: NewAssetConfig[]
    startSelection: (color?: string) => void
    stopSelection: () => void
    filteredTestOptions?: TestToolEntry[]
}

const shouldAddAction = (widgetConfig: Record<string, unknown>): boolean =>
    _.some(
        toJS(ChannelsWiTestToolsRegistry.items),
        entry => !_.isFunction(entry?.shouldRender) || entry?.shouldRender(widgetConfig)
    )

const getChannelsTemporalTestTools = (): AssetChartPrototypeRightActionsEntry => {
    const ChannelsTemporalTestTools = {
        key: '',
        component: null,
        shouldAddAction: shouldAddAction
    }
    let ChannelsTemporalTestToolsComponent = null
    try {
        const useMoreOptionsContext = require('live/components/widget/ui/more-options-context').default
        const MoreOptionsSection = require('live/components/widget/more-options-section').default

        ChannelsTemporalTestTools.key = 'tools'

        const Component = (props: Props): ReactElement<typeof MoreOptionsSection> => {
            const { push, back, setHighlightColor, setIsHighlighted, setHideDropDown } = useMoreOptionsContext()
            const widgetContext = useContext(WidgetContext)
            const { chart } = widgetContext
            const { startSelection, stopSelection, assetsConfig: visibleAssets } = chart

            const testToolsRegistry = useRegistry<TestToolEntry>(ChannelsWiTestToolsRegistry)

            const filteredTestOptions = useMemo(
                () =>
                    testToolsRegistry?.filter?.(
                        entry =>
                            (!entry?.shouldRender && entry?.shouldRender !== false) ||
                            (_.isFunction(entry?.shouldRender) && entry?.shouldRender(widgetContext?.widget))
                    ),
                [testToolsRegistry, widgetContext]
            )

            if (!filteredTestOptions.length) {
                return null
            }

            return (
                <MoreOptionsSection
                    label={i18n('Test Tools')}
                    icon={<i className="far fa-tachometer-fastest" />}
                    action={() =>
                        push({
                            component: TestToolWidgetActionContent,
                            props: {
                                ...props,
                                startSelection: startSelection.bind(chart),
                                stopSelection: stopSelection.bind(chart),
                                visibleAssets: visibleAssets,
                                back: back,
                                setHighlightColor: setHighlightColor,
                                setIsHighlighted: setIsHighlighted,
                                setHideDropDown: setHideDropDown,
                                filteredTestOptions: filteredTestOptions
                            }
                        })
                    }
                    hasAnimation={true}
                />
            )
        }
        ChannelsTemporalTestToolsComponent = Component
    } catch (e) {
        ChannelsTemporalTestTools.key = 'test-tools'
        const Component = (props: Props): ReactElement => {
            const testToolsRegistry = useRegistry<TestToolEntry>(ChannelsWiTestToolsRegistry)

            return <TestToolWidgetAction testOptions={testToolsRegistry} {...props} />
        }
        ChannelsTemporalTestToolsComponent = Component
    }

    ChannelsTemporalTestTools.component = ChannelsTemporalTestToolsComponent

    return ChannelsTemporalTestTools
}
export default getChannelsTemporalTestTools
